import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { TimerProvider } from '@/context/TimerContext';
import { ToastProvider, useToast } from '@/components/Toast';
import { ConfirmProvider } from '@/components/ConfirmDialog';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Layout } from '@/components/Layout';
import { reportHandledError } from '@/lib/browser-logger';

const LoginPage = lazy(() => import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const HomePageComponent = lazy(() => import('@/features/home/HomePage').then((m) => ({ default: m.HomePage })));
const ExpenseListPage = lazy(() => import('@/features/expenses/ExpenseList').then((m) => ({ default: m.ExpenseList })));
const ExpenseFormPage = lazy(() => import('@/features/expenses/ExpenseForm').then((m) => ({ default: m.ExpenseForm })));
const TimeTrackingPage = lazy(() => import('@/features/time-tracking/TimeTrackingPage').then((m) => ({ default: m.TimeTrackingPage })));
const ThreadListPage = lazy(() => import('@/features/communication/ThreadList').then((m) => ({ default: m.ThreadList })));
const ThreadDetailPage = lazy(() => import('@/features/communication/ThreadDetail').then((m) => ({ default: m.ThreadDetail })));
const TargetAccountPage = lazy(() => import('@/features/target-accounts/TargetAccountPage').then((m) => ({ default: m.TargetAccountPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Every failed save is shown as a toast; report it so it reaches the log monitor too.
      reportHandledError('mobile.mutation_failed', error, { mutationKey: mutation.options.mutationKey?.join('/') });
      const msg = error instanceof Error ? error.message : 'Something went wrong';
      // Use a custom event so components can listen without prop-drilling
      window.dispatchEvent(new CustomEvent('mobile-mutation-error', { detail: msg }));
    },
  }),
});

function GlobalMutationErrorListener() {
  const { showToast } = useToast();
  useEffect(() => {
    const handler = (e: Event) => {
      const msg = (e as CustomEvent<string>).detail || 'Something went wrong';
      showToast(msg, 'error');
    };
    window.addEventListener('mobile-mutation-error', handler);
    return () => window.removeEventListener('mobile-mutation-error', handler);
  }, [showToast]);
  return null;
}

const Spinner = () => (
  <div className="flex h-full items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
  </div>
);

function AppRoutes() {
  const navigate = useNavigate();
  return (
    <Routes>
      <Route path="/login" element={<Suspense fallback={<Spinner />}><LoginPage /></Suspense>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout title="Home">
              <Suspense fallback={<Spinner />}>
                <HomePageComponent />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses/new"
        element={
          <ProtectedRoute>
            <Layout title="New Expense">
              <Suspense fallback={<Spinner />}>
                <ExpenseFormPage onSuccess={() => navigate('/expenses')} onCancel={() => navigate('/expenses')} />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <Layout title="Expenses">
              <Suspense fallback={<Spinner />}>
                <ExpenseListPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/time"
        element={
          <ProtectedRoute>
            <Layout title="Time Tracking">
              <Suspense fallback={<Spinner />}>
                <TimeTrackingPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:threadId"
        element={
          <ProtectedRoute>
            <Layout title="Message">
              <Suspense fallback={<Spinner />}>
                <ThreadDetailPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Layout title="Messages">
              <Suspense fallback={<Spinner />}>
                <ThreadListPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/target-account"
        element={
          <ProtectedRoute>
            <Layout title="ACH Direct">
              <Suspense fallback={<Spinner />}>
                <TargetAccountPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <ToastProvider>
              <ConfirmProvider>
                <GlobalMutationErrorListener />
                <TimerProvider>
                  <AppRoutes />
                </TimerProvider>
              </ConfirmProvider>
            </ToastProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
